import React, { FC, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import {
  getFriendlyErrorMessageIfErrorOccurred,
  getPrettyDate,
} from 'utils/util/util';
import FeedbackCard from 'views/Feedback/FeedbackCard';
import Loading from 'views/Widgets/Loading';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import { FeedbackApiResponse } from '../types';

type Props = {
  isOpen: boolean;
  feedbackId: number;
  onClosed: () => void;
  onDelete: (feedback: any) => void;
};
const DeleteModal: FC<Props> = ({ isOpen, feedbackId, onClosed, onDelete }) => {
  const proxyParameters = useProxyParameters();
  const { formatMessage, locale } = useIntl();
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );

  const { data, error, status } = useConfirmApi<FeedbackApiResponse>({
    url: `/admin/feedback/${feedbackId}`,
    method: 'GET',
    disabled: !isOpen,
    clearOnChange: true,
    clearOnError: true,
  });

  const handleOnDeleteCallback = useCallback(() => {
    ConfirmAPI.sendRequestToConfirm(
      'DELETE',
      `/admin/feedback/${feedbackId}`,
      proxyParameters,
      (data, error: unknown, hardErrorMessage: unknown) => {
        if (error || hardErrorMessage) {
          setErrorMessage(
            getFriendlyErrorMessageIfErrorOccurred(error, hardErrorMessage)
          );
          return;
        }
        onDelete(data);
        onClosed();
      }
    );
  }, [feedbackId, onClosed, onDelete, proxyParameters]);

  useEffect(() => {
    if (status === 'ERROR') {
      // @ts-expect-error
      if (error?.status === 404) {
        toast.warn(
          formatMessage({
            id: 'app.views.administration.content_administration.feedback_and_recognition_admin.delete_modal.error.feedback_not_owned',
            defaultMessage:
              'You do not have permission to delete this feedback. It may have already been deleted or you may have lost the ownership, since belongs to people you may not have admin capability against anymore.',
          })
        );
      } else {
        toast.error(
          formatMessage({
            id: 'app.views.administration.content_administration.feedback_and_recognition_admin.delete_modal.error.fetching_feedback_data',
            defaultMessage: 'Error fetching feedback data',
          })
        );
      }
    }
  }, [error, formatMessage, status]);

  if (status === 'LOADING') {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.feedback_and_recognition_admin.delete_modal.message.fetching_feedback_data',
          defaultMessage: 'Fetching feedback data',
        })}
      />
    );
  }

  return (
    <>
      {!!data && (
        <ConfirmationDialogModal
          isOpen={isOpen}
          onClosed={onClosed}
          toggle={onClosed}
          confirmCallback={handleOnDeleteCallback}
          title={formatMessage(
            {
              id: 'app.views.administration.content_administration.feedback_and_recognition_admin.delete_modal.title.delete_feeedback',
              defaultMessage:
                'Delete feeedback from {personFullName} made on {date}',
            },
            {
              // @ts-expect-error
              personFullName: data.author_person.full_name,
              // @ts-expect-error
              date: getPrettyDate({ dateString: data.created_at, locale }),
            }
          )}
          description={
            <div>
              <div className="mb-4">
                {formatMessage({
                  id: 'app.views.administration.content_administration.feedback_and_recognition_admin.delete_modal.description.delete_feeedback',
                  defaultMessage:
                    'Are you sure that you want to remove this feedback?',
                })}
              </div>
              <FeedbackCard feedback={data} />
            </div>
          }
          confirmText={formatMessage({
            id: 'app.views.administration.content_administration.feedback_and_recognition_admin.delete_modal.confirm_text.delete',
            defaultMessage: 'Delete',
          })}
          validationErrors={errorMessage}
        />
      )}
    </>
  );
};

export default React.memo(DeleteModal);
